import Hls from 'hls.js';
import type { PlayerStatus, Station } from '@/types/domain';
import { usePlayerStore, useConsentStore } from '@/store/playerStore';
import { useHistoryStore } from '@/store/userStore';
import { AUDIO } from '@/config/product.config';

type Listener = (status: PlayerStatus) => void;

class AudioEngine {
  private audio: HTMLAudioElement | null = null;
  private hls: Hls | null = null;
  private listeners = new Set<Listener>();
  private currentStationId: string | null = null;
  private startedAt: number | null = null;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private streamIndex = 0;
  private retryCount = 0;
  private readonly maxRetries = 2;

  init(): void {
    if (this.audio) return;
    this.audio = new Audio();
    this.audio.preload = 'auto';

    // Apply persisted prefs
    const { volume, isMuted } = usePlayerStore.getState();
    this.audio.volume = isMuted ? 0 : volume;

    this.audio.addEventListener('playing', () => {
      this.clearLoadTimer();
      this.retryCount = 0;
      usePlayerStore.getState().setStatus('playing');
      this.emit('playing');
    });
    this.audio.addEventListener('waiting', () => {
      const st = usePlayerStore.getState();
      if (st.status === 'playing') {
        usePlayerStore.getState().setStatus('buffering');
        this.emit('buffering');
      }
    });
    this.audio.addEventListener('stalled', () => {
      const st = usePlayerStore.getState();
      if (st.status === 'playing') {
        usePlayerStore.getState().setStatus('buffering');
        this.emit('buffering');
      }
    });
    this.audio.addEventListener('canplay', () => {
      const st = usePlayerStore.getState();
      if (st.status === 'buffering' || st.status === 'loading') {
        void this.audio?.play().catch(() => {
          // autoplay rejection — not an error if user hasn't interacted
        });
      }
    });
    this.audio.addEventListener('error', () => {
      this.handleStreamError();
    });
  }

  load(station: Station, source: 'manual' | 'smart_tune' | 'search' | 'favorite'): void {
    const consent = useConsentStore.getState().consent;
    const store = usePlayerStore.getState();

    if (consent !== 'granted') {
      store.loadStation(station, source);
      store.setStatus('blocked');
      this.currentStationId = station.id;
      return;
    }

    this.init();

    store.loadStation(station, source);
    this.currentStationId = station.id;
    this.startedAt = Date.now();
    this.streamIndex = 0;
    this.retryCount = 0;

    useHistoryStore.getState().startSession(station.id, station.name, source);

    this.playStation(station);
  }

  private playStation(station: Station): void {
    if (!this.audio) return;

    this.cleanupPlayback();
    this.clearLoadTimer();

    const stream = station.streams[this.streamIndex];
    if (!stream) {
      usePlayerStore.getState().setError('Çalmak için uygun akış yok.');
      this.emit('error');
      return;
    }

    const streamUrl = stream.resolvedUrl || stream.streamUrl;
    if (window.location.protocol === 'https:' && streamUrl.startsWith('http:')) {
      if (this.streamIndex < station.streams.length - 1) {
        this.streamIndex++;
        this.playStation(station);
        return;
      }

      usePlayerStore.getState().setError(
        'Bu istasyon yalnızca güvenli olmayan HTTP akışı sunuyor. HTTPS sayfalarda tarayıcı bu yayını engeller.',
      );
      this.emit('error');
      return;
    }

    usePlayerStore.getState().setStatus('loading');
    this.emit('loading');

    // Load timeout
    this.loadTimer = setTimeout(() => {
      this.handleStreamError();
    }, AUDIO.loadTimeoutMs);

    if (stream.isHls && Hls.isSupported()) {
      this.playHls(streamUrl);
    } else {
      this.playDirect(streamUrl);
    }
  }

  private playHls(url: string): void {
    if (!this.audio) return;

    this.hls = new Hls({
      enableWorker: true,
      lowLatencyMode: true,
      backBufferLength: 30,
    });

    this.hls.loadSource(url);
    this.hls.attachMedia(this.audio);

    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.hls?.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.hls?.recoverMediaError();
            break;
          default:
            this.handleStreamError();
            break;
        }
      }
    });
  }

  private playDirect(url: string): void {
    if (!this.audio) return;
    this.audio.src = url;
    this.audio.load();
    void this.audio.play().catch(() => {
      // Autoplay may be blocked by browser policy — set to blocked state
      const consent = useConsentStore.getState().consent;
      if (consent !== 'granted') {
        usePlayerStore.getState().setStatus('blocked');
        this.emit('blocked');
      }
    });
  }

  private handleStreamError(): void {
    this.clearLoadTimer();
    this.cleanupPlayback();

    const store = usePlayerStore.getState();
    const station = store.currentStation;
    if (!station) return;

    // Try next stream if available
    if (this.streamIndex < station.streams.length - 1) {
      this.streamIndex++;
      this.retryCount = 0;
      usePlayerStore.getState().setStatus('loading');
      this.playStation(station);
      return;
    }

    // Try full retry from first stream up to maxRetries times
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.streamIndex = 0;
      usePlayerStore.getState().setStatus('loading');
      this.playStation(station);
      return;
    }

    this.retryCount = 0;
    usePlayerStore.getState().setError(
      'Akış bağlanamadı. İstasyon çevrimdışı olabilir.',
    );
    this.emit('error');
  }

  play(): void {
    const st = usePlayerStore.getState();
    const station = st.currentStation;
    if (!station) return;

    const consent = useConsentStore.getState().consent;
    if (consent !== 'granted') {
      // This method is called from an explicit play-button interaction, so it
      // is safe to grant playback permission and start audio here.
      useConsentStore.getState().grant();
    }

    if (!this.audio || this.currentStationId !== station.id) {
      this.init();
      this.currentStationId = station.id;
      this.startedAt = Date.now();
      this.streamIndex = 0;
      this.retryCount = 0;
      useHistoryStore.getState().startSession(
        station.id,
        station.name,
        st.selectionSource ?? 'manual',
      );
      this.playStation(station);
      return;
    }

    if (st.status === 'error') {
      // retry from first stream
      if (st.currentStation) {
        this.streamIndex = 0;
        this.playStation(st.currentStation);
      }
      return;
    }

    void this.audio.play().catch(() => {
      usePlayerStore.getState().setStatus('paused');
    });
    this.emit('playing');
  }

  pause(): void {
    if (this.audio) {
      this.audio.pause();
    }
    usePlayerStore.getState().pause();
    this.emit('paused');
  }

  retry(): void {
    const st = usePlayerStore.getState();
    if (st.currentStation) {
      this.streamIndex = 0;
      this.retryCount = 0;
      this.playStation(st.currentStation);
    }
  }

  previous(): void {
    const st = usePlayerStore.getState();
    const prev = st.previousStation;
    if (prev) {
      this.load(prev, 'manual');
    }
  }

  setVolume(v: number): void {
    usePlayerStore.getState().setVolume(v);
    if (this.audio) {
      this.audio.volume = v;
      if (v > 0 && this.audio.muted) {
        this.audio.muted = false;
      }
    }
  }

  toggleMute(): void {
    usePlayerStore.getState().toggleMute();
    if (this.audio) {
      const { isMuted, volume } = usePlayerStore.getState();
      this.audio.muted = isMuted;
      this.audio.volume = isMuted ? 0 : volume;
    }
  }

  onConsentGranted(): void {
    const st = usePlayerStore.getState();
    if (st.status === 'blocked' && st.currentStation) {
      this.init();
      this.streamIndex = 0;
      this.playStation(st.currentStation);
    }
  }

  onConsentDenied(): void {
    this.clearLoadTimer();
    this.cleanupPlayback();
    if (this.audio) {
      this.audio.pause();
    }
    usePlayerStore.getState().pause();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(status: PlayerStatus): void {
    this.listeners.forEach((l) => l(status));
  }

  private clearLoadTimer(): void {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  }

  private cleanupPlayback(): void {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    if (this.audio) {
      this.audio.removeAttribute('src');
      this.audio.load();
    }
  }

  destroy(): void {
    useHistoryStore.getState().endActiveSession();
    this.clearLoadTimer();
    this.cleanupPlayback();
    if (this.audio) {
      this.audio.removeEventListener('playing', null as unknown as EventListener);
      this.audio = null;
    }
    this.listeners.clear();
  }

  getCurrentStationId(): string | null {
    return this.currentStationId;
  }

  getStartedAt(): number | null {
    return this.startedAt;
  }
}

export const audioEngine = new AudioEngine();
